// FeedbackModal — formulář pro nahlášení bugu / návrhu / zpětné vazby.
// Otevírá se z FeedbackButton v TopBar. Po odeslání zapíše do PG feedback + zvoneček alert pro ownera.

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useEscClose from "../lib/useEscClose.js";
import { useZamekRolovani } from "../lib/obrazovka.js";
import { X, ChevronLeft, Bug, Lightbulb, MessageSquare, Send, AlertCircle, Check, ExternalLink, ImageUp, Trash2, Link as LinkIcon } from "lucide-react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { useNotifications } from "../lib/AlertContext.jsx";

// Načte soubor obrázku, zmenší na max 1600 px a vrátí komprimovaný JPEG data URL.
// Drží velikost nízkou (typicky desítky až stovky KB) ať přenos zvládne i mobil.
//
// 20.8.2026: dekódování primárně přes createImageBitmap — na rozdíl od <img>
// zvládne i HEIC/HEIF z iPhonu (v Safari a v Chrome na iOS). Fallback na <img>
// pro prohlížeče bez createImageBitmap. Výstup je vždy JPEG, takže na server
// odchází jednotný, malý formát bez ohledu na to, co člověk vybral.
async function fileToCompressedDataUrl(file, maxDim = 1600, quality = 0.7) {
  let source = null;
  try {
    source = await createImageBitmap(file);
  } catch {
    // createImageBitmap format nezvladl (nebo neexistuje) — zkus <img>.
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = () => rej(new Error("Nepodařilo se načíst soubor"));
      fr.readAsDataURL(file);
    });
    source = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("Tenhle formát obrázku prohlížeč neumí přečíst. Zkus screenshot nebo JPG/PNG."));
      i.src = dataUrl;
    });
  }
  let width = source.width || source.naturalWidth;
  let height = source.height || source.naturalHeight;
  if (!width || !height) throw new Error("Obrázek se nepodařilo přečíst");
  if (width > maxDim || height > maxDim) {
    const scale = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(source, 0, 0, width, height);
  if (source.close) source.close();
  return canvas.toDataURL("image/jpeg", quality);
}

const MAX_FOTEK = 3;

const TYPY = [
  { value: "bug", label: "Bug", icon: Bug, color: "rose", desc: "Něco nefunguje správně" },
  { value: "navrh", label: "Návrh", icon: Lightbulb, color: "amber", desc: "Co by se dalo zlepšit" },
  { value: "zpetna_vazba", label: "Zpětná vazba", icon: MessageSquare, color: "sky", desc: "Obecná zpětná vazba na práci s aplikací" },
];

const TYP_COLORS = {
  rose: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-700", ring: "ring-rose-500", selected: "bg-rose-100 border-rose-500" },
  amber: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", ring: "ring-amber-500", selected: "bg-amber-100 border-amber-500" },
  sky: { bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-700", ring: "ring-sky-500", selected: "bg-sky-100 border-sky-500" },
};

function FeedbackObsah({ onClose }) {
  useEscClose(onClose); // Esc zavre dialog
  useZamekRolovani(true); // pod otevřeným oknem nesmí rolovat stránka vzadu (iOS)
  const location = useLocation();
  const user = getCurrentUser() || {};
  const { refresh: refreshNotifications } = useNotifications();
  // 19.10.2026 (hlášení #171): rozepsané hlášení se průběžně ukládá jako koncept, aby se
  // po omylem zavřeném okně (klik vedle) neztratilo. Smaže se po odeslání nebo tlačítkem Zahodit.
  const konceptKlic = `fw40_feedback_koncept_${String(user.email || "anon").toLowerCase()}`;
  const [koncept0] = useState(() => {
    try { const k = JSON.parse(localStorage.getItem(konceptKlic) || "null"); return k && (String(k.popis || "").trim() || (k.images || []).length) ? k : null; }
    catch { return null; }
  });
  const [konceptObnoven, setKonceptObnoven] = useState(!!koncept0);
  const [typ, setTyp] = useState(koncept0?.typ || "bug");
  const [popis, setPopis] = useState(koncept0?.popis || "");
  const [screenshotUrl, setScreenshotUrl] = useState(koncept0?.screenshotUrl || "");
  const [imagesData, setImagesData] = useState(Array.isArray(koncept0?.images) ? koncept0.images : []); // komprimované JPEG data URL, max 3 fotky
  const [imgBusy, setImgBusy] = useState(false);
  const [imgErr, setImgErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const stranka = location.pathname + (location.search || "");

  useEffect(() => {
    try {
      if (!popis.trim() && !imagesData.length) { localStorage.removeItem(konceptKlic); return; }
      const k = { typ, popis, screenshotUrl, t: Date.now() };
      try { localStorage.setItem(konceptKlic, JSON.stringify({ ...k, images: imagesData })); }
      catch { localStorage.setItem(konceptKlic, JSON.stringify(k)); } // fotky se nevešly, uloží se aspoň text
    } catch { /* bez úložiště to jen neuloží koncept */ }
  }, [typ, popis, screenshotUrl, imagesData, konceptKlic]);
  const zahoditKoncept = () => {
    try { localStorage.removeItem(konceptKlic); } catch { /* nevadí */ }
    setPopis(""); setScreenshotUrl(""); setImagesData([]); setTyp("bug"); setKonceptObnoven(false);
  };

  // Přijme jeden i víc souborů (galerie na mobilu umí multi-select).
  // HEIC z iPhonu má někdy prázdný file.type, proto pouštíme dál i soubory
  // bez MIME — o výsledku rozhodne dekodér.
  async function handleFiles(files) {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setImgBusy(true); setImgErr(null);
    let chyba = null;
    const nove = [];
    for (const file of list) {
      if (imagesData.length + nove.length >= MAX_FOTEK) { chyba = `Maximálně ${MAX_FOTEK} fotky.`; break; }
      if (file.type && !file.type.startsWith("image/")) { chyba = "Vyber prosím obrázek."; continue; }
      try {
        nove.push(await fileToCompressedDataUrl(file));
      } catch (e) {
        chyba = e.message || "Obrázek se nepodařilo zpracovat";
      }
    }
    if (nove.length) setImagesData((prev) => [...prev, ...nove].slice(0, MAX_FOTEK));
    if (chyba) setImgErr(chyba);
    setImgBusy(false);
  }

  // Vložení screenshotu ze schránky (Ctrl/Cmd+V) — na PC nejrychlejší způsob.
  function handlePaste(e) {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) { e.preventDefault(); handleFiles([f]); return; }
      }
    }
  }

  // ESC pro zavření
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const popisTrimmed = popis.trim();
  const popisValid = popisTrimmed.length >= 10;
  const screenshotTrimmed = screenshotUrl.trim();
  // Nahraný obrázek má přednost — pak se URL nevaliduje. Jinak URL musí být z Lightshot/Imgur.
  const screenshotValid = imagesData.length > 0 || !screenshotTrimmed
    || /^https?:\/\/(prnt\.sc|prntscr\.com|imgur\.com|i\.imgur\.com)\//i.test(screenshotTrimmed);
  const canSubmit = popisValid && screenshotValid && !submitting && !imgBusy;

  async function actuallySubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await apiFetch("/api/feedback", {
        method: "POST",
        body: JSON.stringify({
          typ,
          stranka,
          popis: popisTrimmed,
          screenshot_data: imagesData.length ? imagesData : null,
          screenshot_url: imagesData.length ? null : (screenshotTrimmed || null),
        }),
      });
      const j = await r.json();
      if (!j.ok) {
        setError(j.error || "Neznámá chyba");
        setSubmitting(false);
        setShowConfirm(false);
        return;
      }
      // Instant zvonek refresh — owner uvidi novou notifikaci hned (bez cekani na 30s poll)
      refreshNotifications({ force: true }).catch(() => {});
      try { localStorage.removeItem(konceptKlic); } catch { /* nevadí */ }
      setSubmitResult(j);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
      setShowConfirm(false);
    }
  }

  // Success screen
  if (submitResult) {
    return (
      /* klavesnice-ok: jen poděkování a tlačítko, žádné pole (14. 9. 2026, fáze 6) */
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-dark/50 backdrop-blur-sm sm:items-center" onClick={onClose}>
        <div className="safe-bottom w-full max-w-md animate-sheet-up rounded-t-2xl bg-white p-6 text-center shadow-2xl sm:mx-4 sm:animate-none sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Hlášení odesláno</h2>
          <p className="text-slate-600 mb-6">Owner bude o tomto hlášení upozorněn v zvonečku. Děkujeme.</p>
          <button
            onClick={onClose}
            className="btn btn-primary w-full"
          >
            Zavřít
          </button>
        </div>
      </div>
    );
  }

  // Confirm screen
  if (showConfirm) {
    const typLabel = TYPY.find((t) => t.value === typ)?.label || typ;
    return (
      /* klavesnice-ok: souhrn a dvě tlačítka, žádné pole (14. 9. 2026, fáze 6) */
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-dark/50 backdrop-blur-sm sm:items-center" onClick={() => !submitting && setShowConfirm(false)}>
        <div className="safe-bottom max-h-[calc(var(--vv-vyska,100vh)*0.92)] w-full max-w-md animate-sheet-up overflow-y-auto overscroll-contain rounded-t-2xl bg-white p-4 shadow-2xl sm:mx-4 sm:animate-none sm:rounded-2xl sm:p-6" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-lg font-bold text-slate-900 mb-3">Opravdu odeslat?</h2>
          <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-2 text-sm">
            <div><span className="text-slate-500">Typ:</span> <span className="font-medium">{typLabel}</span></div>
            <div><span className="text-slate-500">Stránka:</span> <span className="font-mono text-xs">{stranka}</span></div>
            <div><span className="text-slate-500">Popis:</span> <span className="text-slate-800">{popisTrimmed.substring(0, 120)}{popisTrimmed.length > 120 ? "…" : ""}</span></div>
            {imagesData.length
              ? <div><span className="text-slate-500">Fotky ({imagesData.length}):</span>
                  <div className="mt-1 flex gap-2">{imagesData.map((d, i) => <img key={i} src={d} alt={`Náhled ${i + 1}`} className="max-h-24 rounded-xl border border-slate-200" />)}</div>
                </div>
              : screenshotTrimmed && <div><span className="text-slate-500">Screenshot:</span> <a href={screenshotTrimmed} target="_blank" rel="noreferrer" className="text-plum-600 hover:underline inline-flex items-center gap-1">odkaz <ExternalLink className="w-3 h-3" /></a></div>}
          </div>
          <p className="text-sm text-slate-600 mb-5">Owner dostane upozornění v zvonečku.</p>
          {error && (
            <div className="mb-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => setShowConfirm(false)}
              disabled={submitting}
              className="btn btn-secondary flex-1"
            >
              Zpět
            </button>
            <button
              onClick={actuallySubmit}
              disabled={submitting}
              className="btn btn-primary flex-1"
            >
              {submitting ? "Odesílám…" : <><Send className="w-4 h-4" /> Odeslat</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  // 4.9.2026: na telefonu přes celou obrazovku, stejně jako panel notifikací.
  // Vlevo nahoře tlačítko zpět, akce drží dole a s obsahem nerolují.
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-white sm:items-center sm:bg-brand-dark/50 sm:backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-lg flex-col bg-white sm:mx-4 sm:h-auto sm:max-h-[90vh] sm:rounded-2xl sm:shadow-2xl"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
        onClick={(e) => e.stopPropagation()}
        onPaste={handlePaste}
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-2.5 sm:px-6 sm:py-4">
          <button
            onClick={onClose}
            aria-label="Zpět"
            title="Zpět"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100 sm:hidden"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="min-w-0 flex-1 truncate text-[17px] font-bold leading-tight text-slate-900 sm:text-xl">Nahlásit / Zpětná vazba</h2>
          <button onClick={onClose} aria-label="Zavřít" className="hidden rounded-xl p-1.5 hover:bg-slate-100 sm:block">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">

        {/* Typ — radio cards */}
        <div className="mb-5">
          {konceptObnoven && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
              <span className="flex-1 min-w-0 text-amber-800">Máš tu rozepsaný koncept z minula.</span>
              <button type="button" onClick={zahoditKoncept} className="shrink-0 min-h-[36px] rounded-lg border border-amber-300 bg-white px-3 text-sm font-semibold text-amber-800 hover:bg-amber-100">Zahodit</button>
            </div>
          )}
          <label className="block text-sm font-semibold text-slate-700 mb-2">Čeho se to týká?</label>
          <div className="grid grid-cols-3 gap-2">
            {TYPY.map((t) => {
              const c = TYP_COLORS[t.color];
              const selected = typ === t.value;
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTyp(t.value)}
                  className={`p-3 border-2 rounded-xl text-left transition-all ${selected ? c.selected + " " + c.text : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
                >
                  <Icon className="w-5 h-5 mb-1.5" />
                  <div className="font-semibold text-sm">{t.label}</div>
                  <div className="text-xs mt-0.5 opacity-80 leading-tight">{t.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Stránka — auto-fill, read-only */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Stránka</label>
          <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono text-slate-700">{stranka}</div>
        </div>

        {/* Popis */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Popis <span className="text-rose-500">*</span>
            <span className="text-xs text-slate-500 ml-2 font-normal">(min 10 znaků)</span>
          </label>
          <textarea
            value={popis}
            onChange={(e) => setPopis(e.target.value)}
            rows={5}
            placeholder={typ === "bug" ? "Co se stalo? Co jsi očekával/a? Jaké kroky vedly k chybě?" : typ === "navrh" ? "Co bys chtěl/a aby aplikace dělala lépe?" : "Co tě napadá k aplikaci?"}
            className={`field w-full resize-none ${konceptObnoven && popis.trim() ? "!border-amber-300 !bg-amber-50/60" : ""}`}
          />
          <div className="text-xs text-slate-500 mt-1">{popisTrimmed.length} znaků {!popisValid && <span className="text-rose-500">, minimum 10</span>}</div>
        </div>

        {/* Screenshot — nahrání obrázku (mobil i PC), vložení ze schránky, nebo odkaz */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Screenshot <span className="text-xs text-slate-500 font-normal">(volitelné)</span>
          </label>

          {imagesData.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {imagesData.map((d, i) => (
                <div key={i} className="relative">
                  <img src={d} alt={`Fotka ${i + 1}`} className="h-24 w-24 rounded-xl border border-slate-200 object-cover" />
                  <button
                    type="button"
                    onClick={() => { setImagesData((prev) => prev.filter((_, x) => x !== i)); setImgErr(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-lg"
                    title="Odebrat fotku"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {imagesData.length < MAX_FOTEK && (
            <>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={imgBusy}
                className={`w-full flex flex-col items-center justify-center gap-1.5 px-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-plum-400 hover:text-plum-600 hover:bg-plum-50/40 transition-colors disabled:opacity-50 ${imagesData.length ? "py-3" : "py-5"}`}
              >
                <ImageUp className="w-6 h-6" />
                <span className="text-sm font-medium">
                  {imgBusy ? "Zpracovávám obrázek…" : imagesData.length ? `Přidat další fotku (${imagesData.length}/${MAX_FOTEK})` : "Nahrát fotky / vyfotit"}
                </span>
                <span className="text-xs text-slate-400">z galerie i foťáku, až {MAX_FOTEK} fotky, na PC jde i Ctrl/Cmd+V</span>
              </button>
              {/* multiple = galerie na mobilu pusti vic fotek najednou. BEZ atributu
                  capture — systém sám nabídne galerii i foťák. accept s výčtem
                  včetně HEIC/HEIF, protože samotné image/* na části Androidů
                  HEIC soubory z výběru vyšedí. */}
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*,.heic,.heif,image/heic,image/heif"
                className="hidden"
                onChange={(e) => { const fs = e.target.files; if (fs && fs.length) handleFiles(fs); }}
              />
              {/* Záloha: odkaz z Lightshot (když nechtějí nahrávat) */}
              <details className="mt-2 group">
                <summary className="text-xs text-slate-500 cursor-pointer inline-flex items-center gap-1 hover:text-slate-700 list-none">
                  <LinkIcon className="w-3 h-3" /> nebo vlož odkaz na screenshot
                </summary>
                <input
                  type="text"
                  value={screenshotUrl}
                  onChange={(e) => setScreenshotUrl(e.target.value)}
                  placeholder="https://prnt.sc/abc123"
                  className="field mt-2 w-full font-mono"
                />
                {!screenshotValid && <div className="text-xs text-rose-500 mt-1">Musí být z Lightshot (prnt.sc, prntscr.com) nebo Imgur</div>}
                {screenshotValid && screenshotTrimmed && <div className="text-xs text-emerald-600 mt-1 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Validní URL</div>}
              </details>
            </>
          )}
          {imgErr && <div className="text-xs text-rose-500 mt-1">{imgErr}</div>}
        </div>

        {/* User info — read-only */}
        <div className="mb-5 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
          Hlásí: <span className="font-medium text-slate-800">{user.name || user.email || "—"}</span>
          {user.role && <span className="ml-2 px-2 py-0.5 bg-white border border-slate-300 rounded text-slate-700">{user.role}</span>}
        </div>

        </div>

        {/* Akce drží dole, s obsahem nerolují */}
        <div className="safe-bottom flex shrink-0 items-center gap-2 border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
          <button
            onClick={onClose}
            className="btn btn-secondary flex-1"
          >
            Zrušit
          </button>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSubmit}
            className="btn btn-primary flex-1"
          >
            <Send className="w-4 h-4" /> Pokračovat
          </button>
        </div>
      </div>
    </div>
  );
}

// 4.9.2026: horní lišta má backdrop-blur, a to z ní dělá containing block pro
// position:fixed. Okno se proto kreslilo jen uvnitř lišty a na telefonu bylo
// useknuté. Renderujeme ho portálem rovnou do <body>, kde fixed drží viewport.
export default function FeedbackModal(props) {
  if (typeof document === "undefined") return null;
  return createPortal(<FeedbackObsah {...props} />, document.body);
}
